/**
 * The Survivor Game tab's horizontally scrolling round strip (S1). The
 * selected chip is kept scrolled into view, mirroring
 * `apps/app/src/features/scores/DateStrip.tsx`.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import type { JSX } from "react";
import { useEffect, useRef } from "react";

import { SURVIVOR_ROUNDS_LABEL } from "./survivorGameCopy";
import type { typeRoundChip } from "./survivorGameModel";

export type typeRoundStripProps = {
  chips: readonly typeRoundChip[];
  selectedRoundId: string;
  onSelectRound: (roundId: string) => void;
};

export function RoundStrip(props: typeRoundStripProps): JSX.Element | null {
  const tablistRef = useRef<HTMLDivElement>(null);
  const chipKey = props.chips.map((chip) => chip.roundId).join(",");

  useEffect(() => {
    const container = tablistRef.current;
    if (container === null) return;
    let target: HTMLElement | null;
    try {
      target = container.querySelector<HTMLElement>(
        `[data-round-id="${props.selectedRoundId}"]`,
      );
    } catch {
      target = null;
    }
    if (target === null) return;
    target.scrollIntoView({ inline: "center", block: "nearest" });
  }, [props.selectedRoundId, chipKey]);

  if (props.chips.length === 0) {
    return null;
  }

  return (
    <div
      ref={tablistRef}
      role="tablist"
      aria-label={SURVIVOR_ROUNDS_LABEL}
      data-testid="survivor-round-strip"
      className="flex gap-2 overflow-x-auto [scrollbar-width:none]"
    >
      {props.chips.map((chip) => {
        const isSelected = chip.roundId === props.selectedRoundId;
        return (
          <button
            key={chip.roundId}
            type="button"
            role="tab"
            aria-selected={isSelected}
            data-testid={`survivor-round-chip-${chip.roundId}`}
            data-round-id={chip.roundId}
            className={cn(
              "flex min-h-touch-target min-w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-md px-3 py-2",
              isSelected
                ? "border-2 border-primary bg-primary-tint"
                : "border border-hairline bg-surface",
              FOCUS_RING,
            )}
            onClick={() => {
              props.onSelectRound(chip.roundId);
            }}
          >
            <span className="text-small font-semibold text-ink-secondary">
              {chip.topLabel}
            </span>
            <span className="text-caption text-ink">{chip.bottomLabel}</span>
          </button>
        );
      })}
    </div>
  );
}
